export type ServicePage = {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  keywords: string[];
  outcomes: string[];
  faqs: { question: string; answer: string }[];
};

export const servicePages: ServicePage[] = [
  {
    slug: 'website-design',
    title: 'Website Design',
    metaTitle: 'Website Design Services | Orbitra Solution',
    metaDescription: 'Premium website design for businesses that need fast, accessible, conversion-focused digital experiences built with modern architecture.',
    h1: 'Website design for ambitious businesses',
    intro: 'Orbitra Solution designs and builds premium websites with clear strategy, strong information architecture, accessible interfaces, and performance-focused implementation.',
    keywords: ['website design', 'custom websites', 'business website design', 'responsive web design'],
    outcomes: ['Custom visual direction aligned to your brand', 'Responsive implementation for mobile and desktop', 'Technical SEO, analytics, and launch readiness'],
    faqs: [
      { question: 'Do you use templates for website design?', answer: 'No. Orbitra Solution creates custom website experiences around the brand, content, and conversion goals of each business.' },
      { question: 'Can you redesign an existing website?', answer: 'Yes. We can rebuild an existing presence with improved messaging, structure, speed, accessibility, and analytics.' },
    ],
  },
  {
    slug: 'landing-pages',
    title: 'Landing Pages',
    metaTitle: 'Landing Page Design Services | Orbitra Solution',
    metaDescription: 'High-converting landing pages for campaigns, launches, ads, and product offers with technical SEO, analytics, and CTA tracking.',
    h1: 'Landing pages built to convert',
    intro: 'We create focused landing pages with persuasive structure, clear calls to action, fast loading, and measurable analytics events from day one.',
    keywords: ['landing page design', 'campaign landing page', 'conversion landing page', 'product launch page'],
    outcomes: ['Single-goal page strategy', 'CTA and scroll-depth tracking', 'Fast, mobile-first implementation'],
    faqs: [
      { question: 'What makes a landing page different from a website?', answer: 'A landing page is built around one campaign goal, while a full website supports broader discovery, trust, and navigation.' },
      { question: 'Can landing pages connect to ad campaigns?', answer: 'Yes. The analytics architecture is ready for GA4, GTM, Google Ads, Meta, LinkedIn, and TikTok events.' },
    ],
  },
  {
    slug: 'business-websites',
    title: 'Business Websites',
    metaTitle: 'Business Website Development | Orbitra Solution',
    metaDescription: 'Professional business websites with service pages, contact funnels, local SEO, schema, and measurable lead-generation infrastructure.',
    h1: 'Business websites with substance',
    intro: 'Orbitra Solution builds business websites that explain what you do, earn trust, support search discovery, and turn qualified visitors into inquiries.',
    keywords: ['business website', 'small business website', 'company website design', 'lead generation website'],
    outcomes: ['Clear service architecture', 'Local and organic SEO foundations', 'Lead-ready contact flows'],
    faqs: [
      { question: 'Do business websites include SEO?', answer: 'Yes. We include metadata, sitemap planning, structured data, accessibility basics, and performance-sensitive implementation.' },
      { question: 'Can you write the website content?', answer: 'We can help shape concise, natural, search-friendly content around your positioning and customer questions.' },
    ],
  },
  {
    slug: 'portfolio-websites',
    title: 'Portfolio Websites',
    metaTitle: 'Portfolio Website Design | Orbitra Solution',
    metaDescription: 'Premium portfolio websites for founders, creators, studios, and professionals who need elegant presentation and fast discovery.',
    h1: 'Portfolio websites that frame your best work',
    intro: 'We design portfolio websites that make work easy to understand, explore, and remember across search engines, social previews, and AI answer surfaces.',
    keywords: ['portfolio website', 'creative portfolio', 'professional portfolio website', 'studio portfolio'],
    outcomes: ['Case-study-ready content structure', 'Premium visual presentation', 'Shareable previews for social and messaging platforms'],
    faqs: [
      { question: 'Can a portfolio website include case studies?', answer: 'Yes. We can structure work pages around challenge, approach, outcome, technology, and business impact.' },
      { question: 'Is a portfolio site good for SEO?', answer: 'Yes, when projects use descriptive content, internal links, image metadata, schema, and fast page delivery.' },
    ],
  },
  {
    slug: 'ai-chatbots',
    title: 'AI Chatbots',
    metaTitle: 'AI Chatbot Development | Orbitra Solution',
    metaDescription: 'Custom AI chatbot development for websites, WhatsApp, lead qualification, support automation, and business workflows.',
    h1: 'AI chatbots for useful customer conversations',
    intro: 'Orbitra Solution designs chatbot systems that answer questions, qualify leads, reduce repetitive work, and integrate with your actual business processes.',
    keywords: ['AI chatbot', 'website chatbot', 'WhatsApp chatbot', 'lead qualification chatbot'],
    outcomes: ['Conversation design mapped to business goals', 'Lead capture and routing workflows', 'Analytics-ready interaction tracking'],
    faqs: [
      { question: 'Can an AI chatbot work on my website?', answer: 'Yes. We can design a website chatbot that answers common questions and routes qualified leads to your team.' },
      { question: 'Can chatbots integrate with WhatsApp?', answer: 'Yes. We can plan WhatsApp chatbot flows for inquiries, appointment requests, and support workflows.' },
    ],
  },
  {
    slug: 'ai-automation',
    title: 'AI Automation',
    metaTitle: 'AI Automation Services | Orbitra Solution',
    metaDescription: 'AI automation systems for workflows, operations, lead management, scheduling, email automation, and internal productivity.',
    h1: 'AI automation that removes operational drag',
    intro: 'We build practical automation systems that connect tools, trigger workflows, summarize data, route leads, and give teams time back.',
    keywords: ['AI automation', 'workflow automation', 'business automation', 'LLM automation'],
    outcomes: ['Workflow discovery and automation mapping', 'Tool integrations for real business processes', 'Measurable reduction in repetitive tasks'],
    faqs: [
      { question: 'What can AI automation handle?', answer: 'AI automation can support lead capture, email workflows, appointment scheduling, reporting, content operations, and internal task routing.' },
      { question: 'Do you use no-code automation tools?', answer: 'When appropriate, we can integrate tools such as n8n, Make, or Zapier alongside custom software.' },
    ],
  },
  {
    slug: 'brand-identity',
    title: 'Brand Identity',
    metaTitle: 'Brand Identity Design | Orbitra Solution',
    metaDescription: 'Brand identity systems including logo direction, typography, color systems, visual language, and launch-ready digital guidelines.',
    h1: 'Brand identity systems with digital discipline',
    intro: 'Orbitra Solution builds visual identities that are memorable, usable, and consistent across websites, social channels, sales assets, and product interfaces.',
    keywords: ['brand identity', 'logo design', 'visual identity', 'brand guidelines'],
    outcomes: ['Identity direction and visual system', 'Typography and color usage guidance', 'Digital-first brand consistency'],
    faqs: [
      { question: 'What is included in brand identity?', answer: 'A brand identity can include logo direction, color, typography, layout rules, image direction, and practical usage guidance.' },
      { question: 'Can you apply identity to a website?', answer: 'Yes. We can turn the identity into a polished website and reusable design system.' },
    ],
  },
  {
    slug: 'graphic-design',
    title: 'Graphic Design',
    metaTitle: 'Graphic Design Services | Orbitra Solution',
    metaDescription: 'Graphic design for social media, posters, flyers, business cards, brochures, campaign visuals, and polished brand assets.',
    h1: 'Graphic design for sharp brand communication',
    intro: 'We create graphic assets that keep your brand clear and consistent across campaigns, social posts, print material, and digital touchpoints.',
    keywords: ['graphic design', 'social media design', 'poster design', 'brochure design'],
    outcomes: ['Campaign-ready visual assets', 'Consistent brand execution', 'Formats prepared for digital and print use'],
    faqs: [
      { question: 'Do you design social media posts?', answer: 'Yes. We design individual posts and visual packs for launches, promotions, and ongoing brand communication.' },
      { question: 'Can you design print assets?', answer: 'Yes. We can prepare business cards, posters, flyers, brochures, and related materials.' },
    ],
  },
  {
    slug: 'ui-ux-design',
    title: 'UI/UX Design',
    metaTitle: 'UI/UX Design Services | Orbitra Solution',
    metaDescription: 'UI/UX design for websites, apps, dashboards, and conversion flows with accessible structure and user-centered product thinking.',
    h1: 'UI/UX design for clear digital products',
    intro: 'Orbitra Solution designs interfaces that help users understand, decide, and act with confidence across websites, dashboards, apps, and product workflows.',
    keywords: ['UI UX design', 'interface design', 'user experience design', 'dashboard UX'],
    outcomes: ['User flow and interface planning', 'Accessible component patterns', 'Design direction ready for implementation'],
    faqs: [
      { question: 'Can you design dashboards and SaaS interfaces?', answer: 'Yes. We design focused operational interfaces with clear hierarchy, reusable patterns, and efficient workflows.' },
      { question: 'Do UI/UX projects include development?', answer: 'They can. Orbitra Solution can take the work from strategy and interface design through production implementation.' },
    ],
  },
];

export const servicePageMap = new Map(servicePages.map((page) => [page.slug, page]));

